/**
 * `renderPieceLabel` — presentation: turn a BE-numbered pièce into the
 * lawyer-facing in-text citation string (« Pièce n° 7 »).
 *
 * This is the SAME presentation split as the article path
 * (`domain/citation/usecases/render.ts`): the BE owns the *numbering*
 * (which exhibit is ordinal 7, which party-prefix) — the client only
 * formats the display string. No legal decision happens here.
 *
 * Language axis:
 *   - `fr` → « Pièce n° 7 » / « Pièce adverse n° 7 » / « Pièce commune n° 7 »
 *   - `en` → "Exhibit no. 7" / "Opposing exhibit no. 7" / "Joint exhibit no. 7"
 *   - `ar` → **D3 pending lawyer sign-off** (the Arabic pièce convention
 *     « المستند رقم » vs « الوثيقة رقم » is unvalidated, plan §13 D3). The
 *     add-in has no Arabic rendering infra yet (i18n bundle = ledger F5),
 *     so we render the FR convention as the documented interim rather than
 *     invent an unvalidated Arabic string (CLAUDE.md §5: no LLM defaults).
 */

import type { RefParty } from '../entity'
import type { ChipLanguage } from '@/domain/citation-chip/entity'

const NBSP = '\u00A0'

const FR_PARTY_WORD: Record<RefParty, string> = {
  mine: 'Pièce',
  adverse: 'Pièce adverse',
  joint: 'Pièce commune',
}

const EN_PARTY_WORD: Record<RefParty, string> = {
  mine: 'Exhibit',
  adverse: 'Opposing exhibit',
  joint: 'Joint exhibit',
}

export function renderPieceLabel(
  party: RefParty,
  ordinal: number,
  language: ChipLanguage,
): string {
  if (language === 'en') {
    return `${EN_PARTY_WORD[party]} no.${NBSP}${ordinal}`
  }
  // fr + ar (ar = FR interim pending D3 sign-off) — NBSP keeps
  // « Pièce n° 7 » atomic so it never wraps mid-citation.
  return `${FR_PARTY_WORD[party]} n°${NBSP}${ordinal}`
}
